import React, { useEffect } from 'react'
import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom'
import { ThemeProvider, CssBaseline } from '@mui/material'
import { theme } from '@/theme/theme'
import HeaderSection from '@/components/sections/HeaderSection'
import { FooterSection } from '@/components/sections/FooterSection'
import AnnouncementBar from '@/components/AnnouncementBar'
import { routes } from '@/routes'
import { footerContent } from '@/data'
import {
  initializeAnalytics,
  setAnalyticsConsent,
  trackPageView,
  trackSocialClick,
  isAnalyticsEnabled,
} from '@/utils/analytics'

/**
 * Analytics Tracker Component
 * Tracks page views on route changes
 */
const AnalyticsTracker: React.FC = () => {
  const location = useLocation()

  useEffect(() => {
    // Small delay to ensure analytics is ready
    const timer = setTimeout(() => {
      const currentRoute = routes.find(r => r.path === location.pathname)
      const pageTitle = currentRoute?.label || 'NCESP'
      trackPageView(location.pathname, pageTitle)
    }, 100)

    return () => clearTimeout(timer)
  }, [location])

  return null
}

/**
 * Main App Component
 *
 * Application shell with global header/footer and page routing
 * Following constitutional principles and accessibility guidelines
 */
const App: React.FC = () => {
  // Header navigation handlers
  const handleNavClick = (section: string) => {
    const sectionMap: Record<string, string> = {
      home: 'hero-section',
      about: 'hero-section',
      mission: 'mission-section',
      resources: 'resources-section',
      donate: 'donation-section',
    }

    const sectionId = sectionMap[section]
    if (sectionId) {
      const targetSection = document.querySelector(
        `[data-testid="${sectionId}"]`
      )
      if (targetSection) {
        targetSection.scrollIntoView({ behavior: 'smooth', block: 'start' })
      }
    }
  }

  const handleHeaderDonate = () => {
    // Donate dialog is now opened directly in HeaderSection component
    // No scrolling needed
  }

  const handleHeaderSocialClick = (platform: string, url: string) => {
    trackSocialClick(platform, url)
  }

  // Initialize analytics automatically on mount (no consent required for low-traffic sites)
  useEffect(() => {
    // Auto-consent and initialize for production
    if (isAnalyticsEnabled()) {
      setAnalyticsConsent(true)
      initializeAnalytics().then(() => {
        console.log('[App] Analytics ready')
      })
    }
  }, [])

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />

      <BrowserRouter>
        {/* Analytics Tracker */}
        <AnalyticsTracker />

        {/* Event Announcement Bar */}
        <AnnouncementBar />

        {/* Global Header Navigation */}
        <HeaderSection
          onNavClick={handleNavClick}
          onDonateClick={handleHeaderDonate}
          onSocialClick={handleHeaderSocialClick}
        />

        {/* Page Routes */}
        <Routes>
          {routes.map(route => (
            <React.Fragment key={route.path}>
              <Route path={route.path} element={<route.component />} />
              {/* Render subroutes if they exist */}
              {route.subItems?.map(subRoute => (
                <Route
                  key={subRoute.path}
                  path={subRoute.path}
                  element={<subRoute.component />}
                />
              ))}
            </React.Fragment>
          ))}
        </Routes>

        {/* Global Footer */}
        <FooterSection content={footerContent} />
      </BrowserRouter>
    </ThemeProvider>
  )
}

export default App
